import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  ListAlt as ListAltIcon,
  LockOutlined as LockOutlinedIcon,
  SwapHoriz as SwapHorizIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { tienePermisosModulo, puedeEditarProgramaProductos, puedeAccionProductos } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader } from 'components/financepro';
import { useProductosMaterialContext } from 'context';

/** Misma fila `programa` que la pestaña «Entradas / salidas inventario» en MantenimientoProductos. */
const PROGRAMA_TRANSACCIONES = 'transaccionesinventario';

/** Mismos estilos de campo que `Motores/informemotores.js` (INPUT_SX). */
const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    height: 46,
    minHeight: 46,
    borderRadius: 1.6,
    bgcolor: '#FFFFFF',
    '& fieldset': { borderColor: FP.color.border },
    '&:hover fieldset': { borderColor: FP.color.borderStrong },
    '&.Mui-focused fieldset': { borderColor: FP.color.navy },
  },
  '& .MuiInputLabel-root': { fontSize: '0.86rem' },
};

const outlineBtnSx = {
  borderColor: FP.color.border,
  color: FP.color.textPrimary,
  bgcolor: '#FFFFFF',
  borderRadius: 1.6,
  textTransform: 'none',
  fontWeight: 700,
  '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
};

const primaryBtnSx = {
  bgcolor: FP.color.navy,
  color: '#FFFFFF',
  borderRadius: 1.4,
  boxShadow: 'none',
  textTransform: 'none',
  fontWeight: 700,
  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
};

const cardShellSx = {
  borderRadius: 2.5,
  border: `1px solid ${FP.color.border}`,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  overflow: 'hidden',
};

const encabezadoSeccionSx = {
  px: { xs: 2, md: 3 },
  py: 1.6,
  borderBottom: `1px solid ${FP.color.border}`,
  bgcolor: '#FAFBFE',
  display: 'flex',
  alignItems: 'center',
  gap: 1.2,
};

const iconoTituloSx = {
  width: 30,
  height: 30,
  borderRadius: 1.2,
  bgcolor: FP.color.accentBlueSoft,
  color: FP.color.accentBlue,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const formHeaderTitleSx = {
  fontWeight: 700,
  color: FP.color.textPrimary,
  fontSize: '0.98rem',
};

const cellHeadSx = { fontWeight: 700, fontSize: '0.75rem', color: FP.color.textSecondary };

const fmtFecha = (f) => {
  if (!f) return '—';
  const d = new Date(f);
  return Number.isNaN(d.getTime()) ? String(f) : d.toLocaleDateString('es-DO');
};

const buildHtmlReporteEntrada = (datos) => {
  const {
    empresa = 'Empresa',
    numero = '',
    fecha = '',
    causa = '',
    observaciones = '',
    usuario = '',
    localidad = '',
    items = [],
  } = datos;

  const filas = items
    .map(
      (it) => `
    <tr>
      <td style="padding:8px;border:1px solid #ccc">${String(it.codigo || '—').replace(/</g, '&lt;')}</td>
      <td style="padding:8px;border:1px solid #ccc">${String(it.descripcion || '—').replace(/</g, '&lt;')}</td>
      <td style="padding:8px;border:1px solid #ccc;text-align:center">${String(it.medida || '—').replace(/</g, '&lt;')}</td>
      <td style="padding:8px;border:1px solid #ccc;text-align:right">${Number(it.cantidad || 0)}</td>
    </tr>`
    )
    .join('');

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8"/>
  <title>Entrada de inventario #${String(numero).replace(/</g, '')}</title>
  <style>
    body { font-family: system-ui, Segoe UI, sans-serif; margin: 24px; color: #222; }
    h1 { font-size: 1.2rem; margin: 0 0 8px; }
    .meta { font-size: 0.9rem; color: #444; margin-bottom: 16px; }
    table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
    th { background: #f0f0f0; padding: 8px; border: 1px solid #ccc; text-align: left; }
    th.num { text-align: right; }
    @media print { body { margin: 12mm; } }
  </style>
</head>
<body>
  <h1>${String(empresa).replace(/</g, '&lt;')}</h1>
  <h2 style="font-size:1.05rem;margin:10px 0 6px">Reporte de entrada de inventario</h2>
  <p class="meta">
    <strong>Documento Nº</strong> ${String(numero).replace(/</g, '&lt;')}<br/>
    <strong>Fecha</strong> ${fmtFecha(fecha)}<br/>
    <strong>Causa</strong> ${String(causa || '—').replace(/</g, '&lt;')}<br/>
    <strong>Localidad</strong> ${String(localidad || '—').replace(/</g, '&lt;')}<br/>
    <strong>Usuario</strong> ${String(usuario || '—').replace(/</g, '&lt;')}
  </p>
  ${observaciones ? `<p style="font-size:0.9rem;margin-bottom:12px"><strong>Observaciones:</strong> ${String(observaciones).replace(/</g, '&lt;')}</p>` : ''}
  <table>
    <thead>
      <tr>
        <th>Código</th>
        <th>Descripción</th>
        <th style="text-align:center">Medida</th>
        <th class="num">Cantidad</th>
      </tr>
    </thead>
    <tbody>${filas || '<tr><td colspan="4" style="padding:12px;text-align:center">Sin ítems</td></tr>'}</tbody>
  </table>
  <p style="margin-top:24px;font-size:0.8rem;color:#666">Documento generado desde entradas / salidas de inventario.</p>
</body>
</html>`;
};

const hoyISO = () => {
  const d = new Date();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const inicioMesISO = () => {
  const d = new Date();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-01`;
};

const TransaccionesInventario = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  /** Mismo contexto que `Motores/informemotores.js` (`nombreNegocio`, `roleWeb`, `usuarioLog`, `claveLog`). */
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;

  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = tienePermisosModulo(roleDetalles, PROGRAMA_TRANSACCIONES);
  const puedeModificar = puedeEditarProgramaProductos(roleDetalles, PROGRAMA_TRANSACCIONES);
  const puedeEliminar = puedeAccionProductos(roleDetalles, PROGRAMA_TRANSACCIONES, 'borrar');

  const [tablaInventario, setTablaInventario] = useState('productosnoserializados');
  const [localidades, setLocalidades] = useState([]);
  const [localidad, setLocalidad] = useState('');

  const [subTab, setSubTab] = useState(0);
  const [tipo, setTipo] = useState('E');
  const [fecha, setFecha] = useState(hoyISO);
  const [causa, setCausa] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [lineas, setLineas] = useState([]);
  const [guardando, setGuardando] = useState(false);

  const [busquedaProducto, setBusquedaProducto] = useState('');
  const [opcionesProducto, setOpcionesProducto] = useState([]);
  const [productoSel, setProductoSel] = useState(null);
  const [cantidadAgregar, setCantidadAgregar] = useState('1');

  const [fechaDesde, setFechaDesde] = useState(inicioMesISO);
  const [fechaHasta, setFechaHasta] = useState(hoyISO);
  const [listaEntradas, setListaEntradas] = useState([]);
  const [cargandoLista, setCargandoLista] = useState(false);

  const [dialogEliminar, setDialogEliminar] = useState({ open: false, row: null });

  const payloadBase = useMemo(
    () => ({
      tabla_inventario: tablaInventario || "productosnoserializados",
      
    }),
    [selectedCompany, tablaInventario]
  );

  const cargarLocalidades = useCallback(async () => {
    if (!selectedCompany?.db_empresa) return;
    try {
      const response = await fetch(servidor + '/api/productosbackend_obtenerlocalidades', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      if (response.ok) {
        const data = await response.json();
        const locs = data.localidades || [];
        setLocalidades(locs);
        setLocalidad((prev) => prev || (locs[0]?.texto || ''));
      }
    } catch {
      showError('Error al cargar localidades');
    }
  }, [selectedCompany, usuarioLog, claveLog, servidor, showError]);

  useEffect(() => {
    cargarLocalidades();
  }, [cargarLocalidades]);

  useEffect(() => {
    if (selectedCompany?.anomesfacturacion && !tablaInventario) {
      setTablaInventario("productosnoserializados");
    }
  }, [selectedCompany, tablaInventario]);

  const limpiarPantalla = () => {
    setLineas([]);
    setProductoSel(null);
    setBusquedaProducto('');
    setCantidadAgregar('1');
  };

  const buscarProductosApi = useCallback(
    async (texto) => {
      const tab = tablaInventario || "productosnoserializados";
      if (!selectedCompany?.db_empresa || !tab) return [];
      try {
        const response = await fetch(servidor + '/api/productosbackend_buscarproductos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            tabla_inventario: tab,
            descripcion: texto,
            local: localidad,
          }),
        });
        if (!response.ok) return [];
        const data = await response.json();
        return data.productos || [];
      } catch {
        return [];
      }
    },
    [selectedCompany, usuarioLog, claveLog, servidor, tablaInventario, localidad]
  );

  useEffect(() => {
    const t = setTimeout(async () => {
      const r = await buscarProductosApi(busquedaProducto.trim());
      setOpcionesProducto(r);
    }, 300);
    return () => clearTimeout(t);
  }, [busquedaProducto, buscarProductosApi]);

  useEffect(() => {
    limpiarPantalla();
  }, [localidad]);

  const enriquecerProducto = async (codigo) => {
    const tab = tablaInventario || "productosnoserializados";
    if (!codigo || !tab) return null;
    try {
      const response = await fetch(servidor + '/api/productosbackend_obtenerproducto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tabla_inventario: tab,
          codigo,
        }),
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data.producto || null;
    } catch {
      return null;
    }
  };

  const agregarLinea = async () => {
    if (!puedeModificar) {
      showError('No tiene permiso para registrar movimientos');
      return;
    }
    if (!productoSel?.codigo) {
      showError('Seleccione un producto');
      return;
    }
    const q = parseFloat(String(cantidadAgregar).replace(',', '.'));
    if (!q || q <= 0) {
      showError('Indique una cantidad válida');
      return;
    }
    const det = await enriquecerProducto(productoSel.codigo);
    const descripcion = det?.descripcion || productoSel.texto?.split(' - ').slice(1).join(' - ') || '';
    const medida = det?.medida || '';
    const codigo = String(det?.codigo || productoSel.codigo).trim();

    setLineas((prev) => {
      const idx = prev.findIndex((l) => l.codigo === codigo);
      if (idx >= 0) {
        const copia = [...prev];
        copia[idx] = { ...copia[idx], cantidad: copia[idx].cantidad + q };
        return copia;
      }
      return [...prev, { id: `${Date.now()}-${prev.length}`, codigo, descripcion, medida, cantidad: q }];
    });
    setProductoSel(null);
    setBusquedaProducto('');
    setCantidadAgregar('1');
  };

  const quitarLinea = (id) => {
    setLineas((prev) => prev.filter((l) => l.id !== id));
  };

  const actualizarCantidadLinea = (id, val) => {
    const q = parseFloat(String(val).replace(',', '.'));
    if (Number.isNaN(q) || q <= 0) return;
    setLineas((prev) => prev.map((l) => (l.id === id ? { ...l, cantidad: q } : l)));
  };

  const imprimirEntrada = (cab, detalles) => {
    const html = buildHtmlReporteEntrada({
      empresa: selectedCompany?.name || 'Empresa',
      numero: cab.numero,
      fecha: cab.fecha_crea.toLocaleString('es-DO', { year: 'numeric', month: 'numeric', day: 'numeric' }),
      causa: cab.descripcion,
      observaciones: cab.observaciones || '',
      usuario: cab.user || '',
      localidad: cab.localidad || '',
      items: detalles.map((d) => ({
        codigo: d.item,
        descripcion: d.descripcion,
        cantidad: d.cantidad,
        medida: 'Un',
      })),
    });
    const ventana = window.open('', '_blank');
    if (!ventana) {
      showError('Permita ventanas emergentes para imprimir');
      return;
    }
    ventana.document.write(html);
    ventana.document.close();
    ventana.focus();
    setTimeout(() => {
      ventana.print();
      ventana.close();
    }, 250);
  };

  const handleGuardar = async () => {
    if (!puedeModificar) {
      showError('No tiene permiso para registrar movimientos');
      return;
    }
    const tab = tablaInventario || "productosnoserializados";
    if (!selectedCompany?.db_empresa || !tab) {
      showError('Falta configuración de empresa o tabla de inventario');
      return;
    }
    if (!localidad) {
      showError('Seleccione localidad');
      return;
    }
    if (!causa.trim()) {
      showError('Indique la causa del movimiento');
      return;
    }
    if (!lineas.length) {
      showError('Agregue al menos un producto');
      return;
    }

    setGuardando(true);
    try {
      const response = await fetch(servidor + '/api/productosbackend_registrarmovinventario', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          ...payloadBase,
          tabla_inventario: tab,
          fecha,
          tipo,
          causa: causa.trim(),
          observaciones: observaciones.trim(),
          localidad,
          usuario: String(usuarioLog || '').slice(0, 100) || 'sistema',
          lineas: lineas.map((l) => ({
            codigo: l.codigo,
            descripcion: l.descripcion,
            medida: l.medida,
            cantidad: l.cantidad,
          })),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudo registrar');
        return;
      }
      showSuccess(data.mensaje || 'Movimiento registrado');
      const newNumero = data.numero;
      const newDocumento = data.documento || data.id || 'Ajuste de inventario';
      if (tipo === 'E' && newNumero != null) {
        try {
          const r2 = await fetch(servidor + '/api/productosbackend_obtenermovinventario', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id_empresa: selectedCompany,
              usuario: usuarioLog || '',
              password: claveLog || '',
              ...payloadBase,
              id: newNumero,
              documento: newDocumento,
            }),
          });
          if (r2.ok) {
            const d2 = await r2.json();
            imprimirEntrada(d2.cabecera, d2.detalles || []);
          }
        } catch {
          /* opcional */
        }
      }
      setCausa('');
      setObservaciones('');
      setLineas([]);
      if (subTab === 1) {
        cargarEntradas();
      }
    } catch (error) {
      showError('Error de conexión: ' + error.message);
    } finally {
      setGuardando(false);
    }
  };

  const handleImprimirEntrada = async (id, documento = 'Ajuste de inventario') => {
    if (!puedeLeer) return;
    try {
      const response = await fetch(servidor + '/api/productosbackend_obtenermovinventario', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          ...payloadBase,
          id,
          documento: documento || 'Ajuste de inventario',
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        showError(data.Error || 'No se pudo cargar el movimiento');
        return;
      }
      imprimirEntrada(data.cabecera, data.detalles || []);
    } catch (error) {
      showError('Error de conexión: ' + error.message);
    }
  };

  const cargarEntradas = useCallback(async () => {
    if (!selectedCompany?.db_empresa || !puedeLeer) return;
    setCargandoLista(true);
    try {
      const response = await fetch(servidor + '/api/productosbackend_listarmovinventario_entradas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          fecha_desde: fechaDesde,
          fecha_hasta: fechaHasta,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        showError(data.Error || 'Error al listar');
        setListaEntradas([]);
        return;
      }
      setListaEntradas(data.movimientos || []);
    } catch {
      showError('Error de conexión');
    } finally {
      setCargandoLista(false);
    }
  }, [selectedCompany, usuarioLog, claveLog, servidor, fechaDesde, fechaHasta, showError, puedeLeer]);

  const confirmarEliminar = async () => {
    const row = dialogEliminar.row;
    if (!row || !puedeEliminar) return;
    const tab = tablaInventario || "productosnoserializados";
    try {
      const response = await fetch(servidor + '/api/productosbackend_eliminarmovinventario_entrada', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          ...payloadBase,
          tabla_inventario: tab,
          id: row.id,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        showError(data.Error || 'No se pudo eliminar');
        return;
      }
      showSuccess(data.mensaje || 'Eliminado');
      setDialogEliminar({ open: false, row: null });
      cargarEntradas();
    } catch {
      showError('Error de conexión');
    }
  };

  useEffect(() => {
    if (subTab === 1 && puedeLeer) {
      cargarEntradas();
    }
  }, [subTab, puedeLeer, cargarEntradas]);

  const dangerContainedBtnSx = useMemo(
    () => ({
      bgcolor: FP.color.danger,
      color: '#FFFFFF',
      borderRadius: 1.4,
      boxShadow: 'none',
      textTransform: 'none',
      fontWeight: 700,
      '&:hover': { bgcolor: '#BE123C', boxShadow: 'none' },
      '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
    }),
    []
  );

  const inputSmallSx = useMemo(
    () => ({
      ...INPUT_SX,
      '& .MuiOutlinedInput-root': {
        ...INPUT_SX['& .MuiOutlinedInput-root'],
        height: 40,
        minHeight: 40,
      },
    }),
    []
  );

  if (!selectedCompany?.db_empresa) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Entradas / salidas inventario"
          subtitle="Seleccione una empresa activa"
          showQuickActions={false}
        />
        <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card elevation={0} sx={{ maxWidth: 520, width: '100%', ...cardShellSx, p: { xs: 3, md: 4 }, textAlign: 'center' }}>
            <ListAltIcon sx={{ fontSize: 48, color: FP.color.textMuted, mb: 2 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, mb: 1 }}>Sin empresa seleccionada</Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }}>
              Elija una empresa en la barra superior para registrar movimientos de inventario.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader title="Entradas / salidas inventario" subtitle="Permisos · Acceso restringido" showQuickActions={false} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, sm: 3, md: 4 } }}>
          <Card elevation={0} sx={{ maxWidth: 560, width: '100%', ...cardShellSx, textAlign: 'center', p: { xs: 3, md: 4 } }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso de lectura del programa «transaccionesinventario» en roles (módulo Productos).
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Entradas / salidas inventario"
        subtitle={nombreNegocio ? `${nombreNegocio} · Productos` : 'Productos'}
        cta={
          subTab === 1 ? (
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => cargarEntradas()}
              disabled={cargandoLista}
              sx={{
                borderColor: FP.color.border,
                color: FP.color.textPrimary,
                bgcolor: '#FFFFFF',
                borderRadius: 1.4,
                px: 2,
                height: 38,
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
              }}
            >
              Actualizar lista
            </Button>
          ) : undefined
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={encabezadoSeccionSx}>
            <Box sx={iconoTituloSx}>
              <SwapHorizIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={formHeaderTitleSx}>Operaciones</Typography>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', fontWeight: 600, display: { xs: 'none', md: 'block' } }}>
              Registrar · Consultar entradas
            </Typography>
          </Box>
          <Tabs
            value={subTab}
            onChange={(_, v) => setSubTab(v)}
            sx={{
              minHeight: 48,
              px: 1,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.92rem',
                color: FP.color.textSecondary,
                minHeight: 48,
                px: 2.4,
              },
              '& .MuiTab-root.Mui-selected': { color: FP.color.navy },
              '& .MuiTabs-indicator': { backgroundColor: FP.color.navy, height: 3 },
            }}
          >
            <Tab icon={<EditIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Registrar movimiento" />
            <Tab icon={<ListAltIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Entradas registradas" />
          </Tabs>
        </Card>

        {subTab === 0 && (
          <Card elevation={0} sx={cardShellSx}>
            <Box sx={encabezadoSeccionSx}>
              <Box sx={iconoTituloSx}>
                <EditIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={formHeaderTitleSx}>Movimiento de inventario</Typography>
              <Box sx={{ flex: 1 }} />
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', fontWeight: 600, display: { xs: 'none', lg: 'block' } }}>
                Tipo · Fecha · Localidad · Causa
              </Typography>
            </Box>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.875rem', maxWidth: 900, mb: 2 }}>
                Las entradas aumentan el acumulado de entradas; las salidas ajustan salidas como en facturación.
              </Typography>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={4}>
                  <ToggleButtonGroup
                    exclusive
                    fullWidth
                    value={tipo}
                    onChange={(_, v) => v && setTipo(v)}
                    size="small"
                    disabled={!puedeModificar}
                    sx={{
                      borderRadius: 1.4,
                      border: `1px solid ${FP.color.border}`,
                      '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        fontWeight: 700,
                        py: 1,
                        '&.Mui-selected': {
                          bgcolor: FP.color.navy,
                          color: '#FFFFFF',
                          '&:hover': { bgcolor: FP.color.navyDeep },
                        },
                      },
                    }}
                  >
                    <ToggleButton value="E">Entrada</ToggleButton>
                    <ToggleButton value="S">Salida</ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Fecha"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={fecha}
                    onChange={(e) => setFecha(e.target.value)}
                    disabled={!puedeModificar}
                    sx={INPUT_SX}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth sx={INPUT_SX}>
                    <InputLabel id="ti-localidad">Localidad</InputLabel>
                    <Select
                      labelId="ti-localidad"
                      label="Localidad"
                      value={localidad}
                      onChange={(e) => setLocalidad(e.target.value)}
                      disabled={!puedeModificar}
                    >
                      {localidades.map((l) => (
                        <MenuItem key={l.texto} value={l.texto}>
                          {l.texto}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Causa del movimiento"
                    fullWidth
                    required
                    value={causa}
                    onChange={(e) => setCausa(e.target.value)}
                    placeholder="Ej. Ajuste por auditoría, Merma, Devolución…"
                    InputLabelProps={{ shrink: true }}
                    disabled={!puedeModificar}
                    sx={INPUT_SX}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Observaciones"
                    fullWidth
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    disabled={!puedeModificar}
                    sx={INPUT_SX}
                  />
                </Grid>
              </Grid>

              <Typography sx={{ mb: 1, fontWeight: 700, fontSize: '0.875rem', color: FP.color.textSecondary }}>
                Agregar productos
              </Typography>
              <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={opcionesProducto}
                    getOptionLabel={(o) => o.texto || ''}
                    inputValue={busquedaProducto}
                    onInputChange={(_, v) => setBusquedaProducto(v)}
                    value={productoSel}
                    onChange={(_, v) => setProductoSel(v)}
                    disabled={!puedeModificar}
                    renderInput={(params) => (
                      <TextField {...params} label="Buscar producto" sx={INPUT_SX} placeholder="Escriba para buscar…" />
                    )}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label="Cantidad"
                    fullWidth
                    value={cantidadAgregar}
                    onChange={(e) => setCantidadAgregar(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    disabled={!puedeModificar}
                    sx={INPUT_SX}
                  />
                </Grid>
                <Grid item xs={6} md="auto">
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={agregarLinea} disabled={!puedeModificar} sx={{ ...outlineBtnSx, height: 46 }}>
                    Agregar
                  </Button>
                </Grid>
              </Grid>

              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  mb: 2,
                  border: `1px solid ${FP.color.border}`,
                  borderRadius: 2,
                  overflow: 'auto',
                }}
              >
                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <colgroup>
                    <col style={{ width: '14%' }} />
                    <col />
                    <col style={{ width: 100 }} />
                    <col style={{ width: 120 }} />
                    <col style={{ width: 120 }} />
                  </colgroup>
                  <TableHead
                    sx={{
                      /* Tema: MuiTableHead con display:block rompe el ancho del encabezado. */
                      display: 'table-header-group',
                      p: 0,
                      borderRadius: 0,
                    }}
                  >
                    <TableRow sx={{ bgcolor: FP.color.surfaceAlt, width: '100%' }}>
                      <TableCell sx={{ verticalAlign: 'middle' }}>
                        <Typography component="span" sx={cellHeadSx}>
                          Código
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'middle' }}>
                        <Typography component="span" sx={cellHeadSx}>
                          Descripción
                        </Typography>
                      </TableCell>
                      <TableCell width={100} sx={{ verticalAlign: 'middle' }}>
                        <Typography component="span" sx={cellHeadSx}>
                          Medida
                        </Typography>
                      </TableCell>
                      <TableCell width={120} sx={{ verticalAlign: 'middle' }}>
                        <Typography component="span" sx={cellHeadSx}>
                          Cantidad
                        </Typography>
                      </TableCell>
                      <TableCell align="right" width={120} sx={{ verticalAlign: 'middle' }}>
                        <Typography component="span" sx={cellHeadSx}>
                          Acciones
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineas.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3, color: FP.color.textSecondary }}>
                          No hay líneas. Use «Buscar producto» y «Agregar» para armar el movimiento.
                        </TableCell>
                      </TableRow>
                    ) : (
                      lineas.map((l) => (
                        <TableRow key={l.id} hover>
                          <TableCell sx={{ verticalAlign: 'middle' }}>{l.codigo}</TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>{l.descripcion}</TableCell>
                          <TableCell width={100} sx={{ verticalAlign: 'middle' }}>
                            {l.medida}
                          </TableCell>
                          <TableCell width={120} sx={{ verticalAlign: 'middle' }}>
                            <TextField
                              size="small"
                              type="number"
                              fullWidth
                              inputProps={{ min: 0.0001, step: 'any' }}
                              value={l.cantidad}
                              onChange={(e) => actualizarCantidadLinea(l.id, e.target.value)}
                              disabled={!puedeModificar}
                              sx={inputSmallSx}
                            />
                          </TableCell>
                          <TableCell align="right" width={120} sx={{ verticalAlign: 'middle' }}>
                            <IconButton size="small" color="error" onClick={() => quitarLinea(l.id)} disabled={!puedeModificar} aria-label="Quitar">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button variant="contained" 
                  startIcon={<SaveIcon />} 
                  disabled={!puedeModificar || guardando} 
                  onClick={handleGuardar} 
                  sx={{ ...primaryBtnSx, height: 46 }}>
                  Guardar movimiento
                </Button>
                {tipo === 'E' && (
                  <Typography variant="caption" sx={{ color: FP.color.textMuted, alignSelf: 'center' }}>
                    Tras guardar una entrada, se abrirá el reporte para imprimir.
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {subTab === 1 && (
          <Card elevation={0} sx={cardShellSx}>
            <Box sx={encabezadoSeccionSx}>
              <Box sx={iconoTituloSx}>
                <SearchIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={formHeaderTitleSx}>Filtros de consulta</Typography>
              <Box sx={{ flex: 1 }} />
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', fontWeight: 600, display: { xs: 'none', lg: 'block' } }}>
                Rango de fechas · Consultar
              </Typography>
            </Box>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={6} md={3}>
                  <TextField
                    label="Desde"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={fechaDesde}
                    onChange={(e) => setFechaDesde(e.target.value)}
                    sx={INPUT_SX}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label="Hasta"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={fechaHasta}
                    onChange={(e) => setFechaHasta(e.target.value)}
                    sx={INPUT_SX}
                  />
                </Grid>
                <Grid item xs={12} md="auto">
                  <Button variant="outlined" startIcon={<RefreshIcon />} onClick={cargarEntradas} disabled={cargandoLista} sx={{ ...outlineBtnSx, height: 46 }}>
                    Consultar
                  </Button>
                </Grid>
              </Grid>

              <Box sx={encabezadoSeccionSx}>
                <Box sx={iconoTituloSx}>
                  <ListAltIcon sx={{ fontSize: 18 }} />
                </Box>
                <Typography sx={formHeaderTitleSx}>Entradas registradas</Typography>
              </Box>
              <Box sx={{ p: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 } }}>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    border: `1px solid ${FP.color.border}`,
                    borderRadius: 2,
                    overflow: 'auto',
                  }}
                >
                  <Table size="small">
                    <TableHead
                      sx={{
                        display: 'table-header-group',
                        p: 0,
                        borderRadius: 0,
                      }}
                    >
                      <TableRow sx={{ bgcolor: FP.color.surfaceAlt }}>
                        <TableCell>
                          <Typography sx={cellHeadSx}>Num</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={cellHeadSx}>Documento</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={cellHeadSx}>Fecha</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={cellHeadSx}>Causa</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={cellHeadSx}>Usuario</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={cellHeadSx}>Localidad</Typography>
                        </TableCell>
                        <TableCell align="right" width={120}>
                          <Typography sx={cellHeadSx}>Acciones</Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {listaEntradas.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 3, color: FP.color.textSecondary }}>
                            {cargandoLista ? 'Cargando…' : 'Sin registros en el rango'}
                          </TableCell>
                        </TableRow>
                      ) : (
                        listaEntradas.map((r) => (
                          <TableRow key={`${r.documento || 'doc'}-${r.id}`} hover>
                            <TableCell>{r.id}</TableCell>
                            <TableCell>{r.documento || '—'}</TableCell>
                            <TableCell>{fmtFecha(r.fecha)}</TableCell>
                            <TableCell>{r.causa}</TableCell>
                            <TableCell>{r.usuario || '—'}</TableCell>
                            <TableCell>{r.localidad || '—'}</TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => handleImprimirEntrada(r.id, r.documento)}
                                title="Reimprimir"
                                sx={{ color: FP.color.accentBlue }}
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                              {puedeEliminar && (
                                <IconButton size="small" color="error" onClick={() => setDialogEliminar({ open: true, row: r })} title="Eliminar">
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </CardContent>
          </Card>
        )}

        <Dialog
          open={dialogEliminar.open}
          onClose={() => setDialogEliminar({ open: false, row: null })}
          PaperProps={{
            sx: {
              borderRadius: 2.5,
              border: `1px solid ${FP.color.border}`,
              boxShadow: FP.shadow.md,
            },
          }}
        >
          <DialogTitle
            sx={{
              px: 3,
              py: 2,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              fontWeight: 700,
              fontSize: '1rem',
              color: FP.color.textPrimary,
            }}
          >
            Eliminar entrada de inventario
          </DialogTitle>
          <DialogContent sx={{ pt: 2.5 }}>
            <Typography variant="body2" sx={{ color: FP.color.textSecondary }}>
              Se revertirán las cantidades en inventario (entradas) para el documento Nº {dialogEliminar.row?.id}. ¿Continuar?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={() => setDialogEliminar({ open: false, row: null })} sx={outlineBtnSx}>
              Cancelar
            </Button>
            <Button variant="contained" disabled={!puedeEliminar} onClick={confirmarEliminar} sx={dangerContainedBtnSx}>
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default TransaccionesInventario;
